import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import SEOHead from '@/components/marketing/SEOHead';
import PageHeader from '@/components/marketing/PageHeader';
import CTASection from '@/components/marketing/CTASection';
import FAQAccordion from '@/components/marketing/FAQAccordion';
import FormatCard from '@/components/marketing/FormatCard';
import { Button } from '@/components/ui/button';
import { FORMATS } from '@/data/site-content';
import { breadcrumbJsonLd, serviceJsonLd, graph } from '@/lib/jsonld';

export default function AdFormatDetail() {
  const { slug } = useParams();
  const fmt = FORMATS.find((f) => f.slug === slug);
  if (!fmt) return <Navigate to="/ad-formats" replace />;
  const Icon = fmt.icon;
  const others = FORMATS.filter((f) => f.slug !== fmt.slug).slice(0, 3);

  const faqJsonLd = Array.isArray(fmt.faq) && fmt.faq.length > 0 ? {
    '@type': 'FAQPage',
    mainEntity: fmt.faq.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  } : null;

  const jsonLdNodes = [
    breadcrumbJsonLd([
      { label: 'Home', path: '/' },
      { label: 'Ad Formats', path: '/ad-formats' },
      { label: fmt.title },
    ]),
    serviceJsonLd({
      name: fmt.title,
      description: fmt.short,
      serviceType: 'Ad traffic format',
      url: `/ad-formats/${fmt.slug}`,
    }),
  ];
  if (faqJsonLd) jsonLdNodes.push(faqJsonLd);

  return (
    <>
      <SEOHead
        path={`/ad-formats/${fmt.slug}`}
        jsonLd={graph(...jsonLdNodes)}
      />
      <PageHeader
        breadcrumbs={[{ label: 'Ad Formats', to: '/ad-formats' }, { label: fmt.title }]}
        eyebrow="Traffic Format"
        title={fmt.title}
        subtitle={fmt.short}
      >
        <div className="mt-2 flex flex-wrap gap-3">
          <Button asChild className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl transition-colors">
          <Link to="/apply" data-testid={`ad-format-${fmt.slug}-apply-cta`} className="inline-flex items-center gap-2">Apply as Advertiser <ArrowRight className="h-4 w-4" /></Link>
          </Button>
          <Button asChild variant="outline" className="bg-white/5 text-white border-white/15 hover:bg-white/10 hover:text-white rounded-xl transition-colors">
            <Link to="/ad-formats">All formats</Link>
          </Button>
        </div>
      </PageHeader>

      {/* Overview + benefits */}
      <section className="bg-[#F5F7FB]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 prose-dashbound">
            <h2>What this format is</h2>
            <p>{fmt.short}</p>
            <h2>Best use cases</h2>
            <p><strong>Best for:</strong> {fmt.bestFor}.</p>
            <h2>Key benefits</h2>
            <ul>
              {fmt.benefits.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
            <h2>Recommended verticals</h2>
            <p>{fmt.bestVerticals.join(', ')}.</p>
            <h2>How Ads Dashbound helps optimize it</h2>
            <ul>
              {fmt.optimization.map((o, i) => <li key={i}>{o}</li>)}
            </ul>
          </div>
          <aside className="space-y-4">
            <div className="rounded-2xl bg-white border border-slate-900/10 p-6 shadow-[0_10px_30px_rgba(2,6,23,0.06)]">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#2563EB]/10 text-[#2563EB] border border-[#2563EB]/20">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="font-display mt-4 text-base font-semibold text-slate-900">Quick facts</h3>
              <dl className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-slate-500">Format</dt><dd className="text-slate-900 font-medium">{fmt.title}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-slate-500">Best for</dt><dd className="text-slate-900 font-medium text-right">{fmt.bestFor}</dd></div>
                <div className="flex justify-between gap-2"><dt className="text-slate-500">Verticals</dt><dd className="text-slate-900 font-medium text-right">{fmt.bestVerticals.join(', ')}</dd></div>
              </dl>
            </div>
            <div className="rounded-2xl bg-[#07111F] text-white border border-white/10 p-6">
              <h3 className="font-display text-base font-semibold">Compatible trackers</h3>
              <ul className="mt-3 space-y-2 text-sm text-slate-300">
                {['Voluum', 'RedTrack', 'BeMob', 'Keitaro', 'Binom', 'Postback / S2S'].map((t) => (
                  <li key={t} className="inline-flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#22D3EE]" /> {t}</li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#07111F] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#22D3EE]">FAQ</span>
          <h2 className="font-display mt-2 text-2xl sm:text-3xl font-bold">Frequently asked questions</h2>
          <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 px-5 backdrop-blur-xl">
            <FAQAccordion items={fmt.faq} idPrefix={`fmt-${fmt.slug}`} dark />
          </div>
        </div>
      </section>

      {/* Other formats */}
      <section className="bg-[#F5F7FB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-display text-2xl font-bold text-slate-900">Other formats to consider</h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {others.map((o) => <FormatCard key={o.slug} format={o} />)}
          </div>
        </div>
      </section>
      <CTASection primaryTo="/apply" />
    </>
  );
}
